/*
 * INDRAAZ — bundle tier picker (replaces the Rapi app; see BUNDLES-SETUP.md).
 *
 * Reads the JSON product pool embedded by idz-bundle-tier-picker.liquid,
 * handles tier selection, slot picking, the swap dialog, and the price
 * preview. The actual Add to Cart submission is handled by idz-cart.js
 * (extended there for multi-item forms via data-idz-bundle-form) — this
 * file only maintains the hidden inputs that describe what to add.
 *
 * Extra slots (beyond the current page's own product) start EMPTY — no
 * random default product gets assigned. The customer has to explicitly pick
 * what fills each slot.
 *
 * Everything here runs synchronously with the rest of the page — there is
 * no separate app script to race against, which is the whole point of
 * replacing Rapi.
 */
(function () {
  if (window.__idzBundleBound) return;
  window.__idzBundleBound = true;

  function formatMoney(cents) {
    // This store trades only in INR; matches the "₹339"-style formatting
    // already used across the site rather than pulling in a general
    // multi-currency formatter for a single-currency store.
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  // Finds the elements matching `selector` that belong to THIS picker's own
  // product, by walking up from the picker until an ancestor contains a match
  // (the product section on a product page, the modal's own content in a
  // quick-add popup). A plain document.querySelector grabbed the first match
  // on the whole page instead: on a collection page (no price row of its own)
  // that meant a quick-add popup's price/title never updated when a tier was
  // picked, and on a product page it updated the PAGE's price row instead of
  // the popup's.
  function scopedAll(root, selector) {
    var el = root;
    while (el && el.nodeType === 1) {
      var found = el.querySelectorAll(selector);
      if (found.length) return found;
      el = el.parentElement;
    }
    return [];
  }
  function init(root) {
    // Guards against double-binding the same element — needed once init()
    // can be called more than once per element (see the product-info:loaded
    // listener below), since re-running this whole function on an already-
    // wired root would attach a second set of click/keydown listeners on
    // top of the first.
    if (root.dataset.idzBundleInit === 'true') return;
    root.dataset.idzBundleInit = 'true';

    var poolScript = root.querySelector('[data-idz-bundle-pool]');
    if (!poolScript) return;

    var pool;
    try {
      pool = JSON.parse(poolScript.textContent);
    } catch (e) {
      return;
    }

    // Parsed from a merchant-entered unit label like "15ml" or "1 candle" —
    // the leading number is the unit quantity, whatever text follows it
    // (with any separating space trimmed) becomes the suffix shown after
    // the price ("₹23/ml", "₹450/candle"). Used to show a per-unit price
    // under each tier's label (only present at all when the merchant has
    // "Show per-unit price" enabled — see idz-bundle-tier-picker.liquid).
    var unitLabelMatch = (root.dataset.unitLabel || '').match(/^([\d.]+)\s*(.*)$/);
    var unitMl = unitLabelMatch ? parseFloat(unitLabelMatch[1]) || 0 : 0;
    var unitSuffix = (unitLabelMatch && unitLabelMatch[2]) || 'ml';

    var currentVariantId = pool.currentVariantId;
    var products = pool.products || [];
    var currentProduct = products.filter(function (p) {
      return String(p.id) === String(currentVariantId);
    })[0] || { id: currentVariantId, price: 0, title: '', image: '', priceFormatted: '' };

    var allowSwap = root.dataset.allowSwap === 'true';
    var totalEl = root.querySelector('[data-idz-bundle-total]');
    var form = root.querySelector('[data-idz-bundle-form]');
    var dialog = root.querySelector('[data-idz-bundle-swap-dialog]');
    var dialogList = root.querySelector('[data-idz-bundle-swap-list]');
    var tierEls = root.querySelectorAll('[data-idz-bundle-tier]');

    var currentTierEl = null;
    var currentQuantity = 1;
    var currentDiscountType = 'percentage';
    var currentDiscountValue = 0;

    // discount value is either a % (0-100, up to 2 decimals) or a flat
    // rupee amount off the tier's total, per data-discount-type. Fixed is
    // expressed in whole rupees in the theme editor, so it's converted to
    // cents (Shopify money values are always in cents) before use.
    function discountedTotal(base, type, value) {
      if (type === 'fixed') {
        var offCents = (parseFloat(value) || 0) * 100;
        return Math.max(0, base - offCents);
      }
      var pct = parseFloat(value) || 0;
      return Math.round(base * (1 - pct / 100));
    }
    // slots[0] is always the current product; slots[1..] start as `null`
    // (unfilled) until the customer picks something via the swap dialog.
    var slots = [currentProduct];
    var swapTargetIndex = null;

    // Remembers each tier's own slot picks (keyed by quantity, since each
    // tier has a distinct quantity) across switches — picking products for
    // the 3-pack, then checking the 4-pack, then coming back to the 3-pack
    // should not wipe what was already chosen there.
    var tierSlotsByQty = {};

    // When swapping is off, this product page only ever offers MORE of the
    // one product it's on — every slot in every tier is pre-filled with
    // the current product, with no empty/"Select a product" slot for the
    // customer to fill from elsewhere. That's the whole point of turning
    // swap off: no cross-product choice to make, just quantity.
    function emptySlots(quantity) {
      var list = [currentProduct];
      for (var i = 0; i < quantity - 1; i++) {
        list.push(allowSwap ? null : currentProduct);
      }
      return list;
    }

    function slotsTotal(list) {
      return list.reduce(function (acc, p) {
        return acc + (p ? p.price || 0 : 0);
      }, 0);
    }

    // Real MRP total across the filled slots — each product's own
    // compareAtPrice (falling back to its plain price when there's no
    // compare-at set, or it's lower than the price) — for the
    // strikethrough. Kept separate from slotsTotal/discountedTotal's own
    // selling-price math: the tier discount itself is still a % or flat
    // amount off the real selling price, not off MRP, only the
    // strikethrough figure needs to reflect true MRP.
    function slotsMrpTotal(list) {
      return list.reduce(function (acc, p) {
        if (!p) return acc;
        var mrp = p.compareAtPrice || p.price || 0;
        if (mrp < (p.price || 0)) mrp = p.price || 0;
        return acc + mrp;
      }, 0);
    }

    function hasEmptySlot(list) {
      return list.some(function (p) {
        return p === null;
      });
    }

    // Tier cards no longer show their own price (just a label, and a badge
    // for tiers 2-4) — picking one instead updates the real price/strike-
    // through/discount shown in the page's actual price block, above the
    // tiers, so there's exactly one place the customer reads the number
    // that matters rather than two competing figures on the page.
    function renderMainPrice() {
      var priceRow = scopedAll(root, '.idz-price-row')[0];
      if (!priceRow) return;

      var qty = currentQuantity || 1;
      // The discount itself is still computed off the real selling price
      // (base) — a tier's % or flat-amount off is defined against what the
      // product actually sells for, not its MRP. Only the strikethrough
      // shown to the customer (mrpBase) needs to reflect true MRP, so a
      // product already on sale against its own compare_at_price — before
      // any bundle discount — still shows its full real saving instead of
      // understating it by starting from the selling price.
      var base = currentProduct.price * qty;
      var mrpBase = (currentProduct.compareAtPrice || currentProduct.price || 0) * qty;
      if (mrpBase < base) mrpBase = base;
      var discounted = discountedTotal(base, currentDiscountType, currentDiscountValue);
      var pctOff = mrpBase > 0 ? Math.round((1 - discounted / mrpBase) * 100) : 0;

      var saleEl = priceRow.querySelector('.price__sale .price-item--sale');
      var saleWasEl = priceRow.querySelector('.price__sale .price-item--regular');
      var regularEl = priceRow.querySelector('.price__regular .price-item--regular');

      if (saleEl) saleEl.textContent = formatMoney(discounted);
      if (saleWasEl) saleWasEl.textContent = formatMoney(mrpBase);
      if (regularEl) regularEl.textContent = formatMoney(discounted);

      var offEl = priceRow.querySelector('.idz-price-off');
      if (offEl) {
        if (pctOff > 0) {
          offEl.hidden = false;
          offEl.textContent = pctOff + '% OFF';
        } else {
          offEl.hidden = true;
        }
      }
    }

    // Small per-ml figure under each tier's own label (only when the
    // merchant has "Show per-unit price" on for this bundle) — the one
    // piece of the old on-card pricing that's still useful for comparing
    // tiers against each other, even with the full price moved up to the
    // main price block.
    function renderTierPerMl() {
      if (unitMl <= 0) return;
      tierEls.forEach(function (tierEl) {
        var perMlEl = tierEl.querySelector('[data-idz-tier-per-ml]');
        if (!perMlEl) return;

        var qty = parseInt(tierEl.dataset.quantity, 10) || 1;
        var discountType = tierEl.dataset.discountType || 'percentage';
        var discountValue = tierEl.dataset.discountValue || 0;
        var base = currentProduct.price * qty;
        var discounted = discountedTotal(base, discountType, discountValue);
        var totalMl = unitMl * qty;

        perMlEl.textContent = totalMl > 0 ? formatMoney(discounted / totalMl) + '/' + unitSuffix : '';
      });
    }

    // Percentage-off pill pinned to each tier card's bottom edge — replaces
    // the badge that used to live there (see idz-bundle__tier-badge, now a
    // corner ribbon instead). Computed the same way renderMainPrice works
    // out its own %-off figure, just per-tier instead of for whichever tier
    // is currently selected, so every card states its own saving up front.
    function renderTierOff() {
      tierEls.forEach(function (tierEl) {
        var offEl = tierEl.querySelector('[data-idz-tier-off]');
        if (!offEl) return;

        var qty = parseInt(tierEl.dataset.quantity, 10) || 1;
        var discountType = tierEl.dataset.discountType || 'percentage';
        var discountValue = tierEl.dataset.discountValue || 0;
        var base = currentProduct.price * qty;
        // Same MRP base as the main price row (renderMainPrice) — this
        // used to use the plain selling price, which put a different %
        // here than the one shown once that tier was actually picked
        // above, for any product already on sale against its own
        // compare_at_price before the bundle discount.
        var mrpBase = (currentProduct.compareAtPrice || currentProduct.price || 0) * qty;
        if (mrpBase < base) mrpBase = base;
        var discounted = discountedTotal(base, discountType, discountValue);
        var pctOff = mrpBase > 0 ? Math.round((1 - discounted / mrpBase) * 100) : 0;

        if (pctOff > 0) {
          offEl.hidden = false;
          offEl.textContent = pctOff + '% OFF';
        } else {
          offEl.hidden = true;
        }
      });
    }

    function makeSlotPlaceholder(index) {
      var item = document.createElement('div');
      item.className = 'idz-bundle__slot idz-bundle__slot--empty';

      var icon = document.createElement('span');
      icon.className = 'idz-bundle__slot-icon';
      icon.innerHTML =
        '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M3 7.5L12 3l9 4.5M3 7.5V16.5L12 21M3 7.5L12 12M21 7.5V16.5L12 21M21 7.5L12 12M12 12V21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
      item.appendChild(icon);

      var select = document.createElement('button');
      select.type = 'button';
      select.className = 'button idz-bundle__slot-select';
      select.textContent = 'Select';
      select.addEventListener('click', function () {
        openSwapDialog(index);
      });
      item.appendChild(select);

      return item;
    }

    function makeSlotFilled(p, index) {
      var item = document.createElement('div');
      item.className = 'idz-bundle__slot';

      // With swapping off, every slot is always the same product the page
      // is already on — a repeated thumbnail of it adds nothing and just
      // takes up space, so it only shows when a slot could actually hold a
      // different product.
      if (allowSwap) {
        var img = document.createElement('img');
        img.src = p.image || '';
        img.alt = p.title || '';
        img.loading = 'lazy';
        item.appendChild(img);
      } else {
        item.classList.add('idz-bundle__slot--compact');
      }

      var title = document.createElement('span');
      title.className = 'idz-bundle__slot-title';
      title.textContent = p.title;
      item.appendChild(title);

      // Every filled slot is swappable, including the first — the product
      // the customer landed on doesn't have to stay in its own bundle.
      // Tapping the product itself (image/title) opens the swap dialog to
      // change it; the ✕ is a separate, distinct action that clears the
      // slot back to empty — it does not open anything.
      if (allowSwap) {
        item.classList.add('idz-bundle__slot--swappable');
        item.addEventListener('click', function () {
          openSwapDialog(index);
        });

        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'idz-bundle__slot-swap';
        removeBtn.setAttribute('aria-label', 'Remove product');
        removeBtn.textContent = '✕';
        removeBtn.addEventListener('click', function (event) {
          event.stopPropagation();
          slots[index] = null;
          renderAll();
        });
        item.appendChild(removeBtn);
      }

      return item;
    }

    function renderSlots() {
      tierEls.forEach(function (tierEl) {
        var container = tierEl.querySelector('[data-idz-bundle-slots]');
        if (!container) return;

        // With swapping off, every slot is always just more of this same
        // product — nothing left worth a whole grid of slabs to say it.
        // The tier card itself (label + price) is the whole story, so the
        // slots area doesn't render at all in this mode.
        if (!allowSwap) {
          container.hidden = true;
          container.innerHTML = '';
          return;
        }

        var isActive = tierEl === currentTierEl;

        if (!isActive) {
          container.hidden = true;
          container.innerHTML = '';
          return;
        }

        container.hidden = false;
        container.innerHTML = '';
        slots.forEach(function (p, index) {
          container.appendChild(p ? makeSlotFilled(p, index) : makeSlotPlaceholder(index));
        });
      });
    }

    function renderTotal() {
      if (hasEmptySlot(slots)) {
        if (totalEl) totalEl.textContent = 'Select all products';
        return;
      }
      var base = slotsTotal(slots);
      var discounted = discountedTotal(base, currentDiscountType, currentDiscountValue);
      if (totalEl) totalEl.textContent = formatMoney(discounted);
    }

    function renderForm() {
      if (!form) return;
      form.querySelectorAll('[data-idz-bundle-item-id]').forEach(function (el) {
        el.remove();
      });
      slots.forEach(function (p) {
        if (!p) return;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.setAttribute('data-idz-bundle-item-id', '');
        input.value = p.id;
        form.appendChild(input);
      });
      // The active tier's own label ("30 Days", "Pack of 2") is what the
      // customer actually picked — carried through as a hidden field so
      // idz-cart.js can stamp it onto the line as _Pack Label, rather than
      // the cart later falling back to the variant's own title (often just
      // "Default Title" for a single-variant product, which means nothing
      // to the customer).
      var labelInput = form.querySelector('[data-idz-bundle-label]');
      if (!labelInput) {
        labelInput = document.createElement('input');
        labelInput.type = 'hidden';
        labelInput.setAttribute('data-idz-bundle-label', '');
        labelInput.name = 'idz-bundle-label';
        form.appendChild(labelInput);
      }
      // "15ml" — the product's own base unit size, not the tier's label
      // ("30ml", "Pack of 2", etc). A "buy N of this" pack is N of the
      // SAME underlying 15ml product, so the cart pill should read "15ml",
      // "15ml × 2", "15ml × 3" (matching the real unit size), not repeat
      // the tier's own "30ml"/"45ml" naming which would double up with the
      // "× N" the pill already appends. Falls back to the tier's own label
      // if this product has no unit label configured (see
      // idz-bundle-tier-picker.liquid's "Show per-unit price" setting).
      var unitLabelRaw = (root.dataset.unitLabel || '').trim();
      var tierLabelEl = currentTierEl && currentTierEl.querySelector('.idz-bundle__tier-label');
      labelInput.value = unitLabelRaw || (tierLabelEl ? tierLabelEl.textContent.trim() : '');

      // The base unit's own numeric size + suffix (e.g. 15 / "ml"), carried
      // through separately so the cart can show the pack's TOTAL size too —
      // "30ml (15ml x2)" — not just "15ml x2" on its own. Only meaningful
      // when a real unit label is configured; left blank otherwise (the
      // cart falls back to the plain "label x N" format it already had).
      var unitValueInput = form.querySelector('[data-idz-bundle-unit-value]');
      if (!unitValueInput) {
        unitValueInput = document.createElement('input');
        unitValueInput.type = 'hidden';
        unitValueInput.setAttribute('data-idz-bundle-unit-value', '');
        unitValueInput.name = 'idz-bundle-unit-value';
        form.appendChild(unitValueInput);
      }
      unitValueInput.value = unitLabelRaw && unitMl ? String(unitMl) : '';

      var unitSuffixInput = form.querySelector('[data-idz-bundle-unit-suffix]');
      if (!unitSuffixInput) {
        unitSuffixInput = document.createElement('input');
        unitSuffixInput.type = 'hidden';
        unitSuffixInput.setAttribute('data-idz-bundle-unit-suffix', '');
        unitSuffixInput.name = 'idz-bundle-unit-suffix';
        form.appendChild(unitSuffixInput);
      }
      unitSuffixInput.value = unitLabelRaw && unitMl ? unitSuffix : '';

      // Highest quantity among this product's actual configured tiers
      // (e.g. 4, for a product with tiers 1/2/3/4) — carried through so the
      // cart's pack dropdown offers exactly that many options instead of
      // guessing up to 10 from the variant's own quantity rule, which has
      // nothing to do with how many tiers this product was set up with.
      var maxInput = form.querySelector('[data-idz-bundle-max]');
      if (!maxInput) {
        maxInput = document.createElement('input');
        maxInput.type = 'hidden';
        maxInput.setAttribute('data-idz-bundle-max', '');
        maxInput.name = 'idz-bundle-max';
        form.appendChild(maxInput);
      }
      var maxTierQty = 1;
      tierEls.forEach(function (t) {
        var q = parseInt(t.dataset.quantity, 10) || 1;
        if (q > maxTierQty) maxTierQty = q;
      });
      maxInput.value = String(maxTierQty);
      var submit = form.querySelector('[data-idz-cart-submit]');
      if (submit) {
        submit.disabled = hasEmptySlot(slots) || slots.some(function (p) {
          return p && p.available === false;
        });
      }
    }

    function renderAll() {
      renderMainPrice();
      renderTierPerMl();
      renderTierOff();
      renderSlots();
      renderTotal();
      renderForm();
    }

    // Keeps the tier label appended to the product title (see the title block
    // in main-product.liquid) matching whichever tier is active. A no-op on
    // products that don't have the title suffix turned on (no such span).
    function syncTitleTier(tierEl) {
      var labelEl = tierEl && tierEl.querySelector('.idz-bundle__tier-label');
      if (!labelEl) return;
      var text = labelEl.textContent.trim();
      // Union of the picker's own container and the whole product section:
      // the sticky bar's title lives outside the product info column.
      var titleSpans = Array.prototype.slice.call(scopedAll(root, '[data-idz-title-tier]'));
      var sectionEl = root.closest('.shopify-section');
      if (sectionEl) {
        sectionEl.querySelectorAll('[data-idz-title-tier]').forEach(function (el) {
          if (titleSpans.indexOf(el) === -1) titleSpans.push(el);
        });
      }
      titleSpans.forEach(function (el) {
        el.textContent = text;
      });
    }

    function selectTier(tierEl) {
      // Save whatever is currently picked for the tier being left, so
      // switching back to it later restores those same picks instead of
      // starting over empty.
      if (currentQuantity) tierSlotsByQty[currentQuantity] = slots;

      tierEls.forEach(function (t) {
        t.classList.remove('idz-bundle__tier--active');
        t.setAttribute('aria-checked', 'false');
      });
      tierEl.classList.add('idz-bundle__tier--active');
      tierEl.setAttribute('aria-checked', 'true');
      currentTierEl = tierEl;
      syncTitleTier(tierEl);

      currentQuantity = parseInt(tierEl.dataset.quantity, 10) || 1;
      currentDiscountType = tierEl.dataset.discountType || 'percentage';
      currentDiscountValue = tierEl.dataset.discountValue || 0;
      slots = tierSlotsByQty[currentQuantity] || emptySlots(currentQuantity);
      renderAll();
    }

    tierEls.forEach(function (tierEl) {
      tierEl.addEventListener('click', function (event) {
        // A click that originated on the slot area of an already-active
        // tier shouldn't re-trigger tier selection (which would wipe any
        // slot the customer just picked back to empty).
        if (event.target.closest('[data-idz-bundle-slots]') && tierEl === currentTierEl) return;
        selectTier(tierEl);
      });
      tierEl.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectTier(tierEl);
        }
      });
    });

    function openSwapDialog(index) {
      if (!dialog || typeof dialog.showModal !== 'function') return;
      swapTargetIndex = index;

      // Show every eligible product regardless of what's already picked in
      // other slots — the same product can fill more than one slot (buying
      // multiple units of it), so it should never be hidden as if it were
      // used up.
      dialogList.innerHTML = '';
      products
        .forEach(function (p) {
          var row = document.createElement('div');
          row.className = 'idz-bundle-swap__row';

          var img = document.createElement('img');
          img.src = p.image || '';
          img.alt = '';
          img.loading = 'lazy';
          row.appendChild(img);

          var info = document.createElement('div');
          info.className = 'idz-bundle-swap__info';
          var title = document.createElement('span');
          title.textContent = p.title;
          var price = document.createElement('span');
          price.className = 'idz-bundle-swap__price';
          price.textContent = p.priceFormatted;
          info.appendChild(title);
          info.appendChild(price);
          row.appendChild(info);

          var select = document.createElement('button');
          select.type = 'button';
          select.className = 'button idz-bundle-swap__select';
          select.textContent = 'Select';
          select.disabled = p.available === false;
          select.addEventListener('click', function () {
            if (swapTargetIndex === null) return;
            slots[swapTargetIndex] = p;
            dialog.close();
            renderAll();
          });
          row.appendChild(select);

          dialogList.appendChild(row);
        });

      dialog.showModal();
    }

    var closeBtn = root.querySelector('[data-idz-bundle-swap-close]');
    if (closeBtn && dialog) {
      closeBtn.addEventListener('click', function () {
        dialog.close();
      });
    }
    if (dialog) {
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
    }

    // The merchant marks at most one tier as "preselected" (data-preselected
    // on the tier element); falls back to the first tier (always the plain
    // single item) if none is marked, or if more than one accidentally is.
    var preselected = root.querySelector('[data-idz-bundle-tier][data-preselected="true"]');
    currentTierEl = preselected || tierEls[0] || null;
    tierEls.forEach(function (t) {
      var isChosen = t === currentTierEl;
      t.classList.toggle('idz-bundle__tier--active', isChosen);
      t.setAttribute('aria-checked', isChosen ? 'true' : 'false');
    });
    if (currentTierEl) {
      currentQuantity = parseInt(currentTierEl.dataset.quantity, 10) || 1;
      currentDiscountType = currentTierEl.dataset.discountType || 'percentage';
      currentDiscountValue = currentTierEl.dataset.discountValue || 0;
      slots = emptySlots(currentQuantity);
    }
    renderAll();
  }

  function start() {
    document.querySelectorAll('[data-idz-bundle]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // The quick-add modal (quick-add.js) fetches the product page over AJAX
  // and injects a FRESH copy of it — including its own [data-idz-bundle]
  // tier picker — well after this file's own one-time start() has already
  // run. Without this, that copy's tier buttons never get wired up at all:
  // clicking a tier does nothing (any price change visible is Dawn's own
  // native variant swap, unrelated to this picker), and — because
  // renderForm() never ran either — the hidden slot inputs the product
  // form actually submits stay whatever the server originally rendered,
  // so "Add to cart" silently adds the default/first tier regardless of
  // what was clicked. product-info:loaded is dispatched by Dawn's own
  // <product-info> element (see product-info.js) every time one connects
  // to the DOM — including the modal's injected copy — and bubbles, so a
  // single document-level listener catches every one of them.
  document.addEventListener('product-info:loaded', function (event) {
    var scope = event.target && typeof event.target.querySelectorAll === 'function' ? event.target : document;
    scope.querySelectorAll('[data-idz-bundle]').forEach(init);
  });
})();
